import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import { getAnalyticsWalletAsync } from '../../../api/Report';
import { getGestoresAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { formatearFecha, formatearMonto } from '../../../helpers/formato';
import ReporteAgrupado from '../../../components/reportes/ReporteAgrupado';

const TODOS = 't';
// Enlaces anteriores: el tercer segmento de la ruta era 0 = por nombre, 1 = por estado (Tipo).
const ORDEN_POR_ESTADO = 1;

const dinero = (key, label, total = false) => ({
	key,
	label,
	align: 'center',
	total,
	formatoTotal: formatearMonto,
	valor: (c) => formatearMonto(c[key]),
	excel: (c) => c[key]
});

const fecha = (key, label) => ({
	key,
	label,
	align: 'center',
	valor: (c) => formatearFecha(c[key]),
	excel: (c) => formatearFecha(c[key])
});

// En papel va en negro, como el resto de la hoja.
const lineaCuotas = (color, texto) => (
	<Box component="span" sx={{ display: 'block', color, '@media print': { color: '#000' } }}>
		{texto}
	</Box>
);

// Tipo: D = al día, M = en atraso, V = vencido. "Atraso" es el monto atrasado (no días).
const COLUMNAS = [
	{ key: 'id', label: '#', align: 'center', valor: (c) => c.id, excel: (c) => c.id },
	{ key: 'fullName', label: 'Cliente', align: 'left', texto: true, valor: (c) => c.fullName },
	fecha('fechaEntrega', 'F.Entrega'),
	fecha('fechaVencimiento', 'F.Vencimiento'),
	dinero('montoEntregado', 'M.Entregado', true),
	{
		key: 'description',
		label: 'Plazo',
		align: 'center',
		texto: true,
		valor: (c) => c.description
	},
	dinero('cuotaPagar', 'Cuota'),
	{
		key: 'estadoCuotas',
		label: 'Estado Cuotas',
		align: 'left',
		orden: 'cuotasPendiente',
		valor: (c) => (
			<>
				{lineaCuotas('success.main', `Pagadas: ${c.cuotasPagadas}`)}
				{lineaCuotas('error.main', `Pendientes: ${c.cuotasPendiente}`)}
			</>
		),
		excelColumnas: (c) => ({
			'Cuotas Pagadas': c.cuotasPagadas,
			'Cuotas Pendientes': c.cuotasPendiente
		})
	},
	dinero('totalRecibido', 'T.Recibido'),
	{ key: 'tipo', label: 'Tipo', align: 'center', texto: true, valor: (c) => c.tipo },
	dinero('atraso', 'Atraso'),
	dinero('saldoCapital', 'Saldo C.', true),
	dinero('saldoIC', 'Saldo IC.', true),
	dinero('saldoTotal', 'Saldo T.', true)
];

// Con saldo 0 no hay porcentaje que dar.
const porcentaje = (parte, saldo) =>
	saldo > 0 ? `${((parte / saldo) * 100).toFixed(2)} %` : '—';

// Total Mora es el atraso de los créditos al día o en atraso (sin los vencidos, que van en
// Total Vencido con su saldo total). Ambos sobre el saldo pendiente: capital + I.C. de las
// cuotas no canceladas.
const resumen = (filas) => {
	const suma = (lista, campo) => lista.reduce((total, c) => total + c[campo], 0);
	const saldo = suma(filas, 'saldoCapital') + suma(filas, 'saldoIC');
	const mora = suma(
		filas.filter((c) => c.tipo !== 'V'),
		'atraso'
	);
	const vencido = suma(
		filas.filter((c) => c.tipo === 'V'),
		'saldoTotal'
	);

	return [
		{ label: 'Total Mora', valor: `${formatearMonto(mora)} = ${porcentaje(mora, saldo)}` },
		{
			label: 'Total Vencido',
			valor: `${formatearMonto(vencido)} = ${porcentaje(vencido, saldo)}`
		}
	];
};

const AnalyticsWallet = () => {
	const [data, setData] = useState([]);
	const [gestorName, setGestorName] = useState('Todos');

	const { selectedGestor, selectedSortBy } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const [reporte, gestores] = await Promise.all([
				getAnalyticsWalletAsync(token, {
					selectedSortBy,
					gestorId: selectedGestor === TODOS ? 0 : selectedGestor
				}),
				selectedGestor === TODOS ? null : getGestoresAsync(token)
			]);
			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (selectedGestor !== TODOS) {
				// Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
				const gestor =
					gestores && gestores.statusResponse
						? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
						: null;
				setGestorName(
					gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
				);
			}
		})();
	}, [token, selectedGestor, selectedSortBy, setIsLoading, manejarError]);

	// El servidor manda el nombre del gestor del crédito: también el de uno dado de baja.
	const agruparPor = useCallback((fila) => fila.gestorNombre, []);

	return (
		<ReporteAgrupado
			tituloReporte="Reporte de Análisis de Cartera"
			nombreArchivo="AnalisisCartera"
			parametros={[{ label: 'Gestor Seleccionado', valor: gestorName }]}
			columnas={COLUMNAS}
			data={data}
			agruparPor={agruparPor}
			resumen={resumen}
			ordenInicial={parseInt(selectedSortBy) === ORDEN_POR_ESTADO ? 'tipo' : 'fullName'}
			contexto={{ title, slogan, icon, user, rol }}
		/>
	);
};

export default AnalyticsWallet;
